// @ts-nocheck

import { useEffect, useRef, useState } from 'react';
import {
  getSearchBarSettings,
  Settings,
} from '../../../api/getSearchBarSettings';
import {
  Filter,
  FilterRequest,
  searchProducts,
} from '../../../api/searchProducts';

import { Product } from '../../../types/product';
import Filters from '../../FiltersComponentBuilder/filters-component';
import { OutsideClickHandler, ProductGrid, SEARCH_QUERY_STORAGE_KEY } from '..';
import { ENTERED_FROM_SEARCH_BAR } from '../../../constants/tracking';
import { setTrackingSource } from '../../../utils/tracking-session';
import { safeTrack } from '../../../utils/analytics';
import { useDebouncedCallback } from 'use-debounce';

const noOfColumnsMapper = {
  'one-column': 1,
  'two-columns': 2,
  'three-columns': 3,
};

interface AlfinderModalProps {
  currency: string;
  customer: string;
  query: string;
  store: string;
  provider: string;
  open: boolean;
}

const AlfinderModal: React.FC<AlfinderModalProps> = ({
  currency,
  customer,
  query,
  store,
  provider,
  open,
}) => {
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [openDialog, setOpenDialog] = useState(open);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastSearchTime, setLastSearchTime] = useState<number | null>(null);

  const [searchConfig, setSearchConfig] = useState<Settings | null>(null);
  const [dynamicFilters, setDynamicFilters] = useState<Filter[] | null>(null);
  const isDynamicFiltersEnabled =
    searchConfig?.uiStyles?.isDynamicFiltersEnabled ?? true;
  const [showFilters, setShowFilters] = useState(true);

  const [appliedFilters, setAppliedFilters] = useState<Record<
    string,
    string[] | { from: number; to: number }
  > | null>(null);

  const abortController = useRef<AbortController | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const getSettings = async () => {
    const settings = await getSearchBarSettings(Number(store), provider);
    setSearchConfig(settings);
  };

  const fetchWithFilters = async () => {
    const { products } = await search(query, appliedFilters ?? undefined);
    setSearchResults(products);
  };

  const handleSearchEvent = async (type: string) => {
    const previousQueries = localStorage.getItem(SEARCH_QUERY_STORAGE_KEY);
    const searchedQueries = previousQueries ? JSON.parse(previousQueries) : [];
    const trimmedQuery = query.trim();
    const isNewQuery = !searchedQueries.includes(trimmedQuery);

    if (type === 'product_clicked') {
      safeTrack('search_performed', {
        query: trimmedQuery,
        storeId: store,
        provider,
        customerId: customer,
        type,
      });

      if (isNewQuery) {
        searchedQueries.push(trimmedQuery);
        localStorage.setItem(
          SEARCH_QUERY_STORAGE_KEY,
          JSON.stringify(searchedQueries),
        );
      }
    }
    // For other event types, only track if it's a new query and store it
    else if (isNewQuery) {
      safeTrack('search_performed', {
        query: trimmedQuery,
        storeId: store,
        provider,
        customerId: customer,
        type,
      });

      searchedQueries.push(trimmedQuery);
      localStorage.setItem(
        SEARCH_QUERY_STORAGE_KEY,
        JSON.stringify(searchedQueries),
      );
    }
  };

  const isPriceOnlyFilterWithZeroRange = (filters: Filter[] | null) => {
    if (!filters || filters.length !== 1) return false;
    if (!filters || filters.length !== 1) return false;
    const filter = filters[0];
    return filter.key === 'price' && filter.min === '0' && filter.max === '0';
  };

  const handleProductClick = async (product: Product) => {
    setTrackingSource(ENTERED_FROM_SEARCH_BAR, product.productId.toString());
    await handleSearchEvent('product_clicked');
    safeTrack('product_clicked', {
      query: query,
      storeId: store,
      provider,
      customerId: customer,
      product: product.productId,
      type: 'search_bar',
      productName: product.name.ar,
    });
    window.open(product.url, '_self');
  };

  const transformFilters = (
    appliedFilters: Record<string, string[] | { from: number; to: number }>,
  ): FilterRequest => {
    return {
      filters: Object.entries(appliedFilters)
        .map(([key, value]) => {
          if (
            key === 'price' &&
            typeof value === 'object' &&
            'from' in value &&
            'to' in value
          ) {
            // Handle price filter
            return {
              key,
              from: value.from.toString(),
              to: value.to.toString(),
            };
          } else if (Array.isArray(value) && value.length > 0) {
            // Handle other filters
            return {
              key,
              values: value,
            };
          }
          return null; // Exclude filters with empty arrays or invalid formats
        })
        .filter(Boolean) as FilterRequest['filters'], // Filter out null values
    };
  };

  const search = async (
    query: string,
    filters?: Record<string, string[] | { from: number; to: number }>,
  ) => {
    // Create new AbortController for this request
    abortController.current = new AbortController();

    let transformedFilters;
    transformedFilters = filters ? transformFilters(filters) : undefined;

    const limit = searchConfig?.productsPerColumn
      ? searchConfig.productsPerColumn *
        noOfColumnsMapper[searchConfig?.resultsNumberOfColumns || 'one-column']
      : 1;

    try {
      const response = await searchProducts({
        storeId: store,
        storeType: provider,
        query,
        filters: transformedFilters,
        currency,
        limit,
        signal: abortController.current.signal,
      });
      return {
        products: response.results.products,
        filters: response.results.filters,
      };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          products: [],
          filters: [],
        };
      }
      throw error;
    }
  };

  const handleFilterChange = (
    key: string,
    value: string[] | { from: number; to: number },
  ) => {
    safeTrack('search_bar_filter_changed', {
      query,
      storeId: store,
      provider,
      customerId: customer,
      type: 'search_bar',
      key: key,
    });
    setAppliedFilters((prev) => {
      const updatedFilters = { ...prev, [key]: value };

      // Remove the filter if value is empty or reset to null if no filters remain
      if (Array.isArray(value) && value.length === 0) {
        delete updatedFilters[key];
      }

      // Track as a new search when filters change
      if (query.trim()) {
        void handleSearchEvent('filter_changed');
      }

      const hasFilters = Object.keys(updatedFilters).length > 0;
      return hasFilters ? updatedFilters : null;
    });
  };

  const handleSearch = useDebouncedCallback(async () => {
    setHasSearched(true);
    setLoading(true);
    const { products, filters } = await search(query);

    setSearchResults(products);
    setDynamicFilters(filters);
    if (!openDialog) {
      setOpenDialog(true);
    }

    setLoading(false);
  }, 700);

  useEffect(() => {
    void getSettings();
  }, []);

  useEffect(() => {
    if (query.trim().length >= 3) {
      setAppliedFilters(null);
      setDynamicFilters(null);
      void handleSearch();
    } else {
      setSearchResults([]);
      setDynamicFilters(null);
    }
  }, [query]);

  useEffect(() => {
    if (query.trim().length) {
      void fetchWithFilters();
    }
  }, [appliedFilters]);

  useEffect(() => {
    if (openDialog && searchResults.length > 0) {
      const modalElement = document.querySelector('.search-results-container');
      if (modalElement) {
        const handleScroll = () => {
          void handleSearchEvent('scroll');
          // Remove listener after first scroll
          modalElement.removeEventListener('scroll', handleScroll);
        };
        modalElement.addEventListener('scroll', handleScroll);
        return () => modalElement.removeEventListener('scroll', handleScroll);
      }
    }
  }, [openDialog, searchResults]);

  useEffect(() => {
    setLastSearchTime(Date.now());

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      void handleSearchEvent('timeout');
    }, 7000);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query]);

  return (
    <OutsideClickHandler onOutsideClick={() => setOpenDialog(false)}>
      {openDialog && (
        <div className="elfinder-modal">
          {(Boolean(searchResults && searchResults?.length) ||
            (dynamicFilters &&
              !isPriceOnlyFilterWithZeroRange(dynamicFilters))) &&
            isDynamicFiltersEnabled && (
              <div
                style={{
                  width: '100%',
                  padding: '10px 10px 0 10px',
                  overflowX: 'auto',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#30728f #eaf0f3',
                }}
                className="alfinder-scrollbar"
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '10px',
                    paddingBottom: '10px',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: '40px',
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ marginInlineEnd: '10px' }}
                    >
                      <g opacity="0.5">
                        <path
                          d="M21 4H14"
                          stroke={
                            searchConfig?.uiStyles?.dynamicFiltersPrimaryColor
                          }
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M10 4H3"
                          stroke={
                            searchConfig?.uiStyles?.dynamicFiltersPrimaryColor
                          }
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M21 12H12"
                          stroke={
                            searchConfig?.uiStyles?.dynamicFiltersPrimaryColor
                          }
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M8 12H3"
                          stroke={
                            searchConfig?.uiStyles?.dynamicFiltersPrimaryColor
                          }
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M21 20H16"
                          stroke={
                            searchConfig?.uiStyles?.dynamicFiltersPrimaryColor
                          }
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M12 20H3"
                          stroke={
                            searchConfig?.uiStyles?.dynamicFiltersPrimaryColor
                          }
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M14 2V6"
                          stroke={
                            searchConfig?.uiStyles?.dynamicFiltersPrimaryColor
                          }
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M8 10V14"
                          stroke={
                            searchConfig?.uiStyles?.dynamicFiltersPrimaryColor
                          }
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M16 18V22"
                          stroke={
                            searchConfig?.uiStyles?.dynamicFiltersPrimaryColor
                          }
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </g>
                    </svg>
                    <div
                      style={{
                        fontSize: '16px',
                        color:
                          searchConfig?.uiStyles?.dynamicFiltersPrimaryColor,
                        fontWeight: 700,
                      }}
                    >
                      تصفية النتائج
                    </div>
                  </div>
                  {appliedFilters && (
                    <button
                      onClick={() => setAppliedFilters(null)}
                      style={{
                        color: showFilters
                          ? searchConfig?.uiStyles?.dynamicFiltersPrimaryColor
                          : 'white',
                        backgroundColor: showFilters
                          ? 'white'
                          : searchConfig?.uiStyles?.dynamicFiltersPrimaryColor,
                        borderRadius: '10px',
                        width: '150px',
                        height: '40px',
                        textAlign: 'center',
                        border: showFilters
                          ? `1px solid ${searchConfig?.uiStyles?.dynamicFiltersPrimaryColor}`
                          : 'none',
                        fontSize: 'inherit',
                      }}
                    >
                      إعادة تعيين الفلاتر
                    </button>
                  )}
                </div>
                {dynamicFilters && !loading ? (
                  <div>
                    {showFilters && (
                      <Filters
                        currency={currency}
                        availableFilters={
                          searchConfig?.uiStyles?.allowedDynamicFilters
                        }
                        primaryColor={
                          searchConfig?.uiStyles?.dynamicFiltersPrimaryColor ??
                          '#000000'
                        }
                        countColor={
                          searchConfig?.uiStyles?.dynamicFiltersCountColor ??
                          '#000000'
                        }
                        filters={dynamicFilters}
                        appliedFilters={appliedFilters || {}}
                        onFilterChange={handleFilterChange}
                        language={
                          searchConfig?.uiStyles?.enableEnglishPrices
                            ? 'en-US'
                            : 'ar-SA'
                        }
                      />
                    )}
                  </div>
                ) : (
                  <div className="alfinder-filters-loading-container">
                    <div className="alfinder-filters-loading"></div>
                    <div className="alfinder-filters-loading"></div>
                    <div className="alfinder-filters-loading"></div>
                    <div className="alfinder-filters-loading"></div>
                  </div>
                )}
              </div>
            )}
          <div
            style={{
              height:
                Boolean(searchResults && searchResults.length) ||
                (Boolean(dynamicFilters) &&
                  !isPriceOnlyFilterWithZeroRange(dynamicFilters))
                  ? '320px'
                  : '100px',
              overflowY: 'auto',
            }}
            className="search-results-container"
          >
            <div
              style={{
                width: '100%',
                overflowY: 'auto',
              }}
            >
              {Boolean(searchResults && searchResults.length) ? (
                <>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '10px',
                      marginInlineStart: '10px',
                      marginTop: searchConfig?.uiStyles?.isDynamicFiltersEnabled
                        ? '0px'
                        : '10px',
                    }}
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 23 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{
                        marginInlineEnd: '10px',
                      }}
                    >
                      <path
                        d="M21.7859 20.7039L16.7703 15.5182C17.9778 13.8562 18.6297 11.8332 18.6275 9.75446C18.6275 4.44481 14.4493 0.125 9.31374 0.125C4.17818 0.125 0 4.44481 0 9.75446C0 15.0641 4.17818 19.3839 9.31374 19.3839C11.3243 19.3862 13.281 18.7122 14.8885 17.4638L19.9042 22.6494C20.1581 22.8841 20.4892 23.0093 20.8296 22.9995C21.17 22.9896 21.4939 22.8454 21.7347 22.5964C21.9755 22.3475 22.115 22.0127 22.1245 21.6607C22.134 21.3088 22.0129 20.9664 21.7859 20.7039ZM2.66107 9.75446C2.66107 8.39408 3.05124 7.06426 3.78225 5.93315C4.51325 4.80203 5.55226 3.92044 6.76787 3.39985C7.98349 2.87925 9.32112 2.74304 10.6116 3.00844C11.9021 3.27383 13.0875 3.92892 14.0179 4.89085C14.9483 5.85278 15.5819 7.07836 15.8386 8.41259C16.0953 9.74683 15.9635 11.1298 15.46 12.3866C14.9565 13.6435 14.1038 14.7177 13.0098 15.4735C11.9157 16.2292 10.6295 16.6326 9.31374 16.6326C7.54999 16.6305 5.85909 15.9051 4.61193 14.6157C3.36477 13.3262 2.66318 11.578 2.66107 9.75446Z"
                        fill={searchConfig?.uiStyles?.searchResultsTitleColor}
                      />
                    </svg>
                    <div
                      style={{
                        fontSize: '16px',
                        color: searchConfig?.uiStyles?.searchResultsTitleColor,
                      }}
                    >
                      نتائج البحث ({searchResults.length} منتج)
                    </div>
                  </div>
                  <ProductGrid
                    products={searchResults as Product[]}
                    productsPerColumn={searchConfig?.productsPerColumn || 1}
                    noOfColumns={
                      noOfColumnsMapper[
                        searchConfig?.resultsNumberOfColumns || 'one-column'
                      ]
                    }
                    onProductClick={handleProductClick}
                    searchConfig={searchConfig as Settings}
                    priceColor={searchConfig?.uiStyles?.priceColor ?? ''}
                    discountedPriceColor={
                      searchConfig?.uiStyles?.discountedPriceColor ?? ''
                    }
                    currency={currency}
                  />
                </>
              ) : loading ? (
                <div
                  style={{
                    padding: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%',
                  }}
                >
                  <div className="alfinder-filters-loading"></div>
                </div>
              ) : !loading && hasSearched ? (
                <div
                  style={{
                    padding: '30px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%',
                  }}
                >
                  {searchConfig?.noResultsText}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </OutsideClickHandler>
  );
};

export default AlfinderModal;
